import { push } from "connected-react-router";
import React, { useEffect, useState, useMemo } from "react";
import * as CONST from "../../../app/constants";
import { useDispatch, useSelector } from "react-redux";
import { GetProviceCodeFromName, ValidateString } from "../../../app/utils";
import { Button, ErrorMark, ErrorWithString, Header } from "../../../components/RegularComponents";
import {
  ERROR_STRING,
  VALIDATION_TYPE,
  VALIDATION_ERROR,
  cityErrorString,
  homeAddressErrorString,
  postalCodeErrorString,
} from "../../../app/errors";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import { setMortgageProperty } from "../../../redux/slice/primaryAgencyApplicationSlice";
import TopNavigationBar from "../../TopNavigationBar";
import styles from "../mra.module.scss";

const MORTGAGE_APPLICATION_PROPERTY = "financialRecordsLocation";

export const ConfirmCompanyFinancialLocation = (props) => {
  const { isModal = false, onEditSave = () => {} } = props;
  const dispatch = useDispatch();

  const applicationType = CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE;
  const applicationMode = CONST.APPLICATION_MODE_RENEW;
  const [applicationSaveError, setApplicationSaveError] = useState(false);

  const financialRecordsLocation = useSelector(
    (state) =>
      state.primaryAgencyApplication.application.businessLicenceData
        ?.financialRecordsLocation ?? {}
  );

  const [civicAddress, setCivicAddress] = useState(
    financialRecordsLocation.street ?? ""
  );
  const [civicAddressError, setCivicAddressError] = useState(
    VALIDATION_ERROR.NO_ERROR
  );
  const [city, setCity] = useState(
    financialRecordsLocation.city ?? ""
  );
  const [cityError, setCityError] = useState(VALIDATION_ERROR.NO_ERROR);
  const [showValidationError, setShowValidationError] = useState(false);
  const [province, setProvince] = useState(
    financialRecordsLocation.province ?? ""
  );
  const [provinceError, setProvinceError] = useState(VALIDATION_ERROR.NO_ERROR);
  const [postalCode, setPostalCode] = useState(
    financialRecordsLocation.postalCode ?? ""
  );
  const [postalCodeError, setPostalCodeError] = useState(
    VALIDATION_ERROR.NO_ERROR
  );

  useEffect(() => {
    civicAddressError === VALIDATION_ERROR.NO_ERROR &&
    cityError === VALIDATION_ERROR.NO_ERROR &&
    postalCodeError === VALIDATION_ERROR.NO_ERROR
      ? setShowValidationError(false)
      : setShowValidationError(true);
  }, [civicAddressError, cityError, postalCodeError]);

  const provinceOptionElements = useMemo(() => {
    return Object.keys(CONST.CanadaProvinceList).map((key) => {
      const element = CONST.CanadaProvinceList[key];
      return (
        <option key={key} value={key}>
          {element}
        </option>
      );
    });
  }, []);

  const validateData = () => {
    const ret1 = ValidateString(civicAddress, VALIDATION_TYPE.CIVIC_ADDRESS);
    setCivicAddressError(ret1);

    const ret2 = ValidateString(city, VALIDATION_TYPE.STRING_NON_EMPTY);
    setCityError(ret2);

    const ret3 = ValidateString(GetProviceCodeFromName(province), VALIDATION_TYPE.CANADA_PROVINCE_CODE)
    setProvinceError(ret3);

    const ret4 = ValidateString(postalCode, VALIDATION_TYPE.CANADA_POSTAL_CODE);
    setPostalCodeError(ret4);

    return (
      ret1 === VALIDATION_ERROR.NO_ERROR &&
        ret2 === VALIDATION_ERROR.NO_ERROR &&
        ret3 === VALIDATION_ERROR.NO_ERROR &&
        ret4 === VALIDATION_ERROR.NO_ERROR
    );
  };

  const handleNext = () => {
    setApplicationSaveError(false);

    //reset errors
    setShowValidationError(false);
    setCivicAddressError(VALIDATION_ERROR.NO_ERROR);
    setCityError(VALIDATION_ERROR.NO_ERROR);
    setProvinceError(VALIDATION_ERROR.NO_ERROR);
    setPostalCodeError(VALIDATION_ERROR.NO_ERROR);

    const update = {
      key: MORTGAGE_APPLICATION_PROPERTY,
      value: {
        street: civicAddress,
        city: city,
        postalCode: postalCode,
        province: province,
      },
    };
    dispatch(setMortgageProperty(update));

    if (!validateData()) {
      setShowValidationError(true);
      return;
    }

    dispatch(
      saveBusinessLicenceApplicationData(applicationType, applicationMode)
    )
      .then((data) => {
        dispatch(
          !isModal
            ? push(
                `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_CONFIRM_APPOINTED_AGENT}`
              )
            : onEditSave()
        );
      })
      .catch((err) => {
        setApplicationSaveError(true);
      });
  };

  const handleCivicAddressChange = (e) => {
    setCivicAddress(e.target.value);
    setCivicAddressError(VALIDATION_ERROR.NO_ERROR);
  };

  const handleCityChange = (e) => {
    setCity(e.target.value);
    setCityError(VALIDATION_ERROR.NO_ERROR);
  };

  const handlePostalCodeChange = (e) => {
    setPostalCode(e.target.value);
    setPostalCodeError(VALIDATION_ERROR.NO_ERROR);
  };

  return (
    <div className={!isModal ? "container" : ""}>
      {!isModal && (
        <>
          <TopNavigationBar {...props} />
          {/* Title */}
          <div className="row">
            <div className="col-xs-12 col-lg-9">
              <Header
                style={{ paddingBottom: "30px" }}
                id="company-fiscal-location-header"
                header="Company financial records location in Canada"
              />
            </div>
          </div>
        </>
      )}

      {/* Civic Address */}
      <div style={{ marginTop: "30px" }} className="row">
        <div className="col-lg-8">
          <div className={
            "ns-fieldset" +
            ((civicAddressError !== VALIDATION_ERROR.NO_ERROR)
              ? " has-error ns-fieldset-message-error"
              : "")
          }>
          <fieldset className="ns-fieldset">
            <label
              id={"mortgage-broker-licence-number-label"}
              className="ns-label"
              autoComplete="postal-code"
              htmlFor={"mortgage-broker-licence-number-text"}
            >
              Civic Address
            </label>
            {civicAddressError !== VALIDATION_ERROR.NO_ERROR && (
              <span
                id={"mortgage-broker-licence-number-text-error"}
                className="ns-error-message field-validation-error"
              >
                {ErrorWithString(homeAddressErrorString(civicAddressError))}
              </span>
            )}
            <input
              type="text"
              className={
                "fw-25" +
                (civicAddressError === VALIDATION_ERROR.NO_ERROR
                  ? " ns-input"
                  : " ns-input--error")
              }
              id={"mortgage-broker-licence-number-text"}
              name={"mortgage-broker-licence-number-text"}
              value={civicAddress}
              onChange={handleCivicAddressChange}
            />
          </fieldset>
          </div>
        </div>
      </div>

      {/* City */}
      <div className="row">
        <div className="col-lg-8">
          <div className={
            "ns-fieldset" +
            ((cityError !== VALIDATION_ERROR.NO_ERROR)
              ? " has-error ns-fieldset-message-error"
              : "")
          }>
          <fieldset className="ns-fieldset">
            <label
              id={"mortgage-broker-licence-number-label"}
              className="ns-label"
              autoComplete="postal-code"
              htmlFor={"mortgage-broker-licence-number-text"}
            >
              City / Town / Community
            </label>
            {cityError !== VALIDATION_ERROR.NO_ERROR && (
              <span
                id={"mortgage-broker-licence-number-text-error"}
                className="ns-error-message field-validation-error"
              >
                {ErrorWithString(cityErrorString(cityError))}
              </span>
            )}
            <input
              type="text"
              className={
                "fw-25" +
                (cityError === VALIDATION_ERROR.NO_ERROR
                  ? " ns-input"
                  : " ns-input--error")
              }
              id={"mortgage-broker-licence-number-text"}
              name={"mortgage-broker-licence-number-text"}
              value={city}
              onChange={handleCityChange}
            />
          </fieldset>
          </div>
        </div>
      </div>

      {/* Province Select section */}
      <div className="row">
        <div className="col-lg-8">
          <div
            className={
              "ns-fieldset" +
              ((provinceError !== VALIDATION_ERROR.NO_ERROR) ? " has-error ns-fieldset-message-error" : "")
            }
          >
            <label
              id={"mra-province-select-label"}
              className="ns-label"
              htmlFor={"mra-province-select-input"}
            >
              Province / Territory
            </label>
            {(provinceError !== VALIDATION_ERROR.NO_ERROR) && (
              <span
                id={"mra-province-select-input-error"}
                className="ns-error-message fieldset-validation-error"
              >
                {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_PROVINCE_EMPTY)}
              </span>
            )}
            <div className="select fw-25 inlineDisplay">
              <select
                className={(provinceError !== VALIDATION_ERROR.NO_ERROR) ? "select--error" : ""}
                style={{ width: "25em" }}
                id={"mra-province-select-input"}
                name={"mra-province-select-input"}
                value={province ? GetProviceCodeFromName(province) : ""}
                onChange={(e) => {
                  setProvince(e.target.value);
                  setProvinceError(VALIDATION_ERROR.NO_ERROR)
                }}
              >
                {provinceOptionElements}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Postal Code */}
      <div className="row">
        <div className="col-lg-8">
          <div className={
            "ns-fieldset" +
            ((postalCodeError !== VALIDATION_ERROR.NO_ERROR)
              ? " has-error ns-fieldset-message-error"
              : "")
          }>
          <fieldset className="ns-fieldset">
            <label
              id={"mortgage-broker-licence-number-label"}
              className="ns-label"
              autoComplete="postal-code"
              htmlFor={"mortgage-broker-licence-number-text"}
            >
              Postal code
            </label>
            {postalCodeError !== VALIDATION_ERROR.NO_ERROR && (
              <span
                id={"mortgage-broker-licence-number-text-error"}
                className="ns-error-message field-validation-error"
              >
                {ErrorWithString(postalCodeErrorString(postalCodeError))}
              </span>
            )}
            <input
              type="text"
              className={
                "fw-25" +
                (postalCodeError === VALIDATION_ERROR.NO_ERROR
                  ? " ns-input"
                  : " ns-input--error")
              }
              id={"mortgage-broker-licence-number-text"}
              name={"mortgage-broker-licence-number-text"}
              value={postalCode}
              onChange={handlePostalCodeChange}
            />
          </fieldset>
          </div>
        </div>
      </div>

      {/* Save and continue */}
      <div className="row">
        <div className={"col-lg-2" + (showValidationError ? " has-error" : "")}>
          <Button
            className={`${styles["question_button"]} btn-lg btn-default`}
            style={{ margin: "0 10px 10px 0" }}
            buttonType={"regular_primary"}
            onClick={handleNext}
            text={!isModal ? "Continue" : "Save and close"}
          />
          {applicationSaveError && !isModal && ErrorMark()}
          {applicationSaveError && !isModal && (
            <div className="ns-error-message help-block topMarginNormal">
              <span className="ns-visually-hidden">Error:</span>
              {ERROR_STRING.APPLICATION_SAVE_ERROR}
            </div>
          )}
          {showValidationError && ErrorMark()}
          {showValidationError && (
            <div className="ns-error-message">
              <span className="ns-visually-hidden">Error:</span>
              {ERROR_STRING.CHECK_FIELDS}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
